// The armory's newer weapons, built here (the older ones came from the ship's model): the heavy revolver, the rocket
// launcher, the staff blaster and the two light swords. Each is a hidden source the held and carried copies are cloned
// from, in the weapons' shared frame (barrel +x, top +y, right +z), its parts named the way the rest of the code looks
// for them: _Grip (the firing hand), _Handguard/_Foregrip (the support hand), _Barrel and _Muzzle (where rounds leave),
// _Sight_* (the optics, iron sights at the least), _Blade_Glow (a light sword's blade).
import { Color3, MeshBuilder, StandardMaterial, TransformNode, type Mesh, type Scene } from "@babylonjs/core";

export const ARSENAL_IDS = ["Revolver", "RPG", "Staff", "Saber", "Saber2", "Blaster", "CSMG", "Baton"] as const;   // the last three: made at the armory table (shared/crafting.ts)

export function buildArsenal(sc: Scene) {
  const mat = (name: string, hex: string, glow = false) => { const m = new StandardMaterial("arsenal_" + name, sc); m.diffuseColor = Color3.FromHexString(hex); m.specularColor = new Color3(0.25, 0.25, 0.28);
    if (glow) { m.emissiveColor = Color3.FromHexString(hex); m.disableLighting = true; } return m; };
  const steel = mat("steel", "#2c2f35"), dark = mat("dark", "#151619"), grip = mat("grip", "#101012"), trim = mat("trim", "#8a9099"), olive = mat("olive", "#3e4a34");
  const blue = mat("blue", "#59c8ff", true), green = mat("green", "#6dff8a", true), amber = mat("amber", "#ffb547", true), lens = mat("lens", "#5fe8ff", true);
  const make = (id: string, build: (part: (n: string, m: Mesh, mt: StandardMaterial, x: number, y: number, z: number, rz?: number) => void) => void) => {
    const root = new TransformNode("Range_Weapon_" + id, sc); root.position.set(0, -1000, 0);     // out of the way: only its copies are ever seen
    root.scaling.z = -1;   // the weapons' one frame, as the rack's (glTF's mirror): barrel +x, top +y, right +z, so the hands and the editor's fits sit alike on all
    const part = (n: string, m: Mesh, mt: StandardMaterial, x: number, y: number, z: number, rz = 0) => { m.name = `Weapon_${id}_${n}`; m.parent = root; m.position.set(x, y, z); m.rotation.z += rz; m.material = mt; m.isPickable = false; };
    build(part); root.setEnabled(false);
  };
  const box = (w: number, h: number, d: number) => MeshBuilder.CreateBox("p", { width: w, height: h, depth: d }, sc);
  const rod = (len: number, dia: number, top?: number) => { const m = MeshBuilder.CreateCylinder("p", { height: len, diameter: dia, diameterTop: top ?? dia, tessellation: 16 }, sc); m.rotation.z = -Math.PI / 2; return m; };   // along +x
  make("Revolver", (p) => {
    p("Frame", box(0.12, 0.05, 0.03), steel, 0.02, 0, 0);
    p("Cylinder", rod(0.05, 0.046), dark, 0.03, 0.002, 0);
    p("Barrel", rod(0.17, 0.022), steel, 0.165, 0.012, 0);
    p("Muzzle", rod(0.012, 0.026), dark, 0.255, 0.012, 0);
    p("Rail_Under", box(0.1, 0.008, 0.018), dark, 0.17, -0.006, 0);
    p("Grip", box(0.035, 0.1, 0.032), grip, -0.04, -0.06, 0, 0.28);
    p("Guard", box(0.05, 0.008, 0.012), steel, 0.01, -0.035, 0);
    p("Sight_Front", box(0.006, 0.014, 0.004), trim, 0.24, 0.03, 0);
    p("Sight_Rear", box(0.01, 0.012, 0.016), trim, -0.02, 0.032, 0);
  });
  make("RPG", (p) => {
    p("Barrel", rod(1.0, 0.09), olive, 0.2, 0.05, 0);
    p("Muzzle", rod(0.18, 0.06, 0.12), olive, 0.78, 0.05, 0);          // the warhead in the front of the tube
    p("Butt", rod(0.06, 0.1), dark, -0.31, 0.05, 0);
    p("Grip", box(0.032, 0.1, 0.03), grip, 0, -0.05, 0, 0.2);
    p("Foregrip", box(0.03, 0.09, 0.03), grip, 0.3, -0.04, 0);
    p("Handguard", box(0.2, 0.012, 0.05), dark, 0.3, 0.004, 0);
    p("Sight_Base", box(0.09, 0.045, 0.035), dark, 0.05, 0.12, 0);
    p("Sight_Lens", box(0.004, 0.03, 0.026), lens, 0.096, 0.12, 0);
  });
  make("Staff", (p) => {
    p("Barrel", rod(1.6, 0.03), steel, 0.2, 0, 0);
    p("Muzzle", rod(0.08, 0.05), amber, 1.02, 0, 0);                    // the emitter: bolts leave here; it is also the striking end
    p("Butt", rod(0.06, 0.045), trim, -0.6, 0, 0);
    p("Grip", rod(0.14, 0.042), grip, 0, 0, 0);
    p("Handguard", rod(0.16, 0.042), grip, 0.35, 0, 0);
    p("Sight_Front", box(0.005, 0.018, 0.004), trim, 0.9, 0.022, 0);
    p("Sight_Rear", box(0.01, 0.016, 0.014), trim, 0.12, 0.024, 0);
  });
  make("Saber", (p) => {
    p("Grip", rod(0.26, 0.036), dark, 0, 0, 0);
    p("Emitter", rod(0.03, 0.044), trim, 0.135, 0, 0);
    p("Blade_Glow", rod(0.9, 0.024), blue, 0.6, 0, 0);
    p("Handguard", rod(0.05, 0.04), steel, 0.08, 0, 0);
  });
  // the armory table's own (crafting, 2026-10-17): a tuned blaster pistol with glowing coils, a compact SMG with a red dot and
  // a green cell, a stun baton (its charged tip is named a blade, so it gets no barrel or sights)
  make("Blaster", (p) => {
    p("Frame", box(0.14, 0.045, 0.03), steel, 0.03, 0, 0);
    p("Barrel", rod(0.11, 0.026), dark, 0.13, 0.008, 0);
    p("Coil_A", rod(0.012, 0.034), blue, 0.105, 0.008, 0);
    p("Coil_B", rod(0.012, 0.034), blue, 0.15, 0.008, 0);
    p("Muzzle", rod(0.014, 0.03), blue, 0.19, 0.008, 0);
    p("Grip", box(0.034, 0.095, 0.03), grip, -0.025, -0.06, 0, 0.25);
    p("Guard", box(0.05, 0.008, 0.012), steel, 0.015, -0.032, 0);
    p("Rail_Under", box(0.08, 0.008, 0.018), dark, 0.1, -0.017, 0);
    p("Sight_Front", box(0.006, 0.014, 0.004), trim, 0.17, 0.028, 0);
    p("Sight_Rear", box(0.01, 0.012, 0.016), trim, -0.02, 0.028, 0);
  });
  make("CSMG", (p) => {
    p("Receiver", box(0.26, 0.06, 0.04), dark, 0.02, 0, 0);
    p("Barrel", rod(0.12, 0.024), steel, 0.21, 0.01, 0);
    p("Muzzle", rod(0.02, 0.032), amber, 0.275, 0.01, 0);
    p("Grip", box(0.032, 0.1, 0.03), grip, -0.06, -0.065, 0, 0.25);
    p("Mag_Cell", box(0.03, 0.09, 0.026), green, 0.04, -0.07, 0);
    p("Foregrip", box(0.03, 0.07, 0.028), grip, 0.14, -0.055, 0);
    p("Handguard", box(0.1, 0.012, 0.042), steel, 0.15, -0.03, 0);
    p("Stock", box(0.12, 0.03, 0.02), olive, -0.17, -0.005, 0);
    p("Sight_Base", box(0.04, 0.03, 0.03), steel, 0, 0.045, 0);
    p("Sight_Lens", box(0.004, 0.02, 0.02), lens, 0.02, 0.045, 0);
  });
  make("Baton", (p) => {
    p("Grip", rod(0.24, 0.034), grip, 0, 0, 0);
    p("Handguard", rod(0.04, 0.042), steel, 0.13, 0, 0);
    p("Shaft", rod(0.32, 0.026), steel, 0.31, 0, 0);
    p("Blade_Glow", rod(0.06, 0.032), blue, 0.5, 0, 0);
  });
  make("Saber2", (p) => {
    p("Grip", rod(0.42, 0.04), dark, -0.04, 0, 0);
    p("Emitter", rod(0.035, 0.05), trim, 0.19, 0, 0);
    p("Blade_Glow", rod(1.2, 0.028), green, 0.81, 0, 0);
    p("Handguard", rod(0.06, 0.044), steel, 0.12, 0, 0);
  });
}
